import React, { useState, useEffect, useRef } from 'react'
import { Icon, message } from 'antd'
import { T, fmtSec } from './SharedLayout'

/**
 * InlineAudioPlayer
 *
 * A compact, stylable audio player used in segment rows and the edit modal.
 */
const InlineAudioPlayer = ({
  src,
  label,
  accent = T.blue,
  compact = false
}) => {
  const [playing, setPlaying] = useState(false)
  const [pct, setPct] = useState(0)
  const [cur, setCur] = useState(0)
  const [dur, setDur] = useState(0)
  const aRef = useRef(null)
  const rafRef = useRef(null)

  useEffect(() => {
    if (!src) return
    const a = new Audio(src)
    aRef.current = a
    a.addEventListener('loadedmetadata', () => setDur(a.duration))
    a.addEventListener('ended', () => {
      setPlaying(false)
      setPct(0)
      setCur(0)
      cancelAnimationFrame(rafRef.current)
    })
    return () => {
      a.pause()
      a.src = ''
      cancelAnimationFrame(rafRef.current)
    }
  }, [src])

  const tick = () => {
    const a = aRef.current
    if (!a) return
    setCur(a.currentTime)
    setPct(a.duration ? (a.currentTime / a.duration) * 100 : 0)
    rafRef.current = requestAnimationFrame(tick)
  }

  const toggle = () => {
    const a = aRef.current
    if (!a) return
    if (playing) {
      a.pause()
      setPlaying(false)
      cancelAnimationFrame(rafRef.current)
    } else {
      a.play().catch(() => message.error('Could not play'))
      setPlaying(true)
      rafRef.current = requestAnimationFrame(tick)
    }
  }

  const seek = e => {
    const a = aRef.current
    if (!a || !a.duration) return
    const r = e.currentTarget.getBoundingClientRect()
    a.currentTime =
      Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * a.duration
  }

  if (compact)
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <button
          onClick={toggle}
          style={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            padding: 0,
            border: `1px solid ${playing ? accent : T.border}`,
            background: playing ? accent : T.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
            transition: 'all 0.13s'
          }}
        >
          <Icon
            type={playing ? 'pause' : 'caret-right'}
            style={{
              fontSize: 9,
              color: playing ? '#fff' : T.inkLight,
              marginLeft: playing ? 0 : 1
            }}
          />
        </button>
        <div
          onClick={seek}
          style={{
            flex: 1,
            height: 3,
            background: T.borderLight,
            borderRadius: 2,
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            minWidth: 44,
            maxWidth: 68
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: '0 auto 0 0',
              width: `${pct}%`,
              background: accent,
              borderRadius: 2,
              transition: 'width 0.08s linear'
            }}
          />
        </div>
        <span
          style={{
            fontSize: 10,
            color: T.inkLight,
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          {fmtSec(cur)}
        </span>
      </div>
    )

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 12px',
        borderRadius: 8,
        border: `1px solid ${T.border}`,
        background: T.bg
      }}
    >
      <button
        onClick={toggle}
        style={{
          width: 30,
          height: 30,
          borderRadius: '50%',
          padding: 0,
          flexShrink: 0,
          border: `1.5px solid ${playing ? accent : T.border}`,
          background: playing ? accent : T.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.13s'
        }}
      >
        <Icon
          type={playing ? 'pause' : 'caret-right'}
          style={{
            fontSize: 12,
            color: playing ? '#fff' : T.inkSub,
            marginLeft: playing ? 0 : 2
          }}
        />
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        {label && (
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: accent,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 5
            }}
          >
            {label}
          </div>
        )}
        <div
          onClick={seek}
          style={{
            height: 3,
            background: T.borderLight,
            borderRadius: 2,
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: '0 auto 0 0',
              width: `${pct}%`,
              background: accent,
              borderRadius: 2,
              transition: 'width 0.08s linear'
            }}
          />
        </div>
      </div>
      <span
        style={{
          fontSize: 11,
          color: T.inkLight,
          fontVariantNumeric: 'tabular-nums',
          flexShrink: 0
        }}
      >
        {fmtSec(cur)} / {fmtSec(dur)}
      </span>
    </div>
  )
}

export default InlineAudioPlayer
